import React, { useEffect, useRef, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import {
  Card,
  Col,
  Row,
  Descriptions,
  List,
  PageHeader,
  Tag,
  Button,
  Upload,
  Input,
  Dropdown,
  Select,
  InputNumber,
  Modal,
  Space,
  message as antdMessage,
} from "antd";

// Icons
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  NumberOutlined,
  SignalFilled,
} from "@ant-design/icons";

import { Link, useHistory, useParams } from "react-router-dom";
import moment from "moment";

// Firebase
import firebase from "firebase/compat/app";
import getFileIcon from "../../../modules/helpers/fileIcon";
import { senderLabel } from "../../../modules/helpers/sender";

import RecipientsTable from "./RecipientsTable";
import {
  AiFillClockCircle,
  AiFillDatabase,
  BiBuildings,
  BsFillPersonFill,
  BsPeopleFill,
  BsTrashFill,
  IoIosColorPalette,
  IoMdMail,
  MdLocalPostOffice,
  RiFileCopy2Line,
  RiFileCopyFill,
  RiFileCopyLine,
} from "react-icons/all";
import ChangeStatusDropdown from "./ChangeStatusDropdown";
import AuditTimeline from "../Journal/AuditTimeline";
import { stamp } from "../../../modules/helpers/audit";

const LabelContainer = ({ ...props }) => {
  return <div style={{ display: "flex", alignItems: "center", gap: "4px" }} {...props} />;
};

function TagStatus({ status }) {
  switch (status) {
    case "scheduled":
      return (
        <Tag icon={<ClockCircleOutlined />} color="processing">
          Programmé
        </Tag>
      );
    default:
      return (
        <Tag icon={<CheckCircleOutlined />} color="success">
          Envoyé
        </Tag>
      );
  }
}

export default function Attachments() {
  const history = useHistory();

  const [message, setMessage] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const user = useSelector((state) => state.user, shallowEqual);
  const [updateTrigger, setUpdateTrigger] = useState(0);
  const [showHandledModal, setShowHandledModal] = useState(false);
  const [countdown, setCountdown] = useState(300); // 5 minutes in seconds
  const [previousStatus, setPreviousStatus] = useState(null);
  const [isStatusChangeInitiator, setIsStatusChangeInitiator] = useState(false);
  const [isReserving, setIsReserving] = useState(false);
  const [generatingMode, setGeneratingMode] = useState(null); // null | "lot" | "preview"
  // Dernières valeurs enregistrées des champs opérateur (comparées avant d'écrire)
  const persistedRef = useRef({});

  const { id, company } = useParams();

  // Configure Firestore
  const db = firebase.firestore();
  const messageDocRef = () => db.collection("companies").doc(company).collection("messages").doc(id);

  // AR tracking functions
  const [batchReserve] = useState(() =>
    firebase.app().functions("europe-west1").httpsCallable("batchReserveTrackingNumbers")
  );
  const [generateLiasses] = useState(() =>
    firebase.app().functions("europe-west1").httpsCallable("generateARLiasses", { timeout: 300000 })
  );

  async function handleBatchReserve() {
    setIsReserving(true);
    try {
      const result = await batchReserve({ companyId: company, messageId: id });
      // `international` : destinataires à l'étranger, exclus des plages nationales (incident EFFIA
      // du 07/10/2026 : ne plus annoncer « tous ont déjà un numéro » quand ils sont exclus)
      const { reserved, alreadyReserved, international = 0 } = result.data;
      if (reserved > 0) {
        antdMessage.success(`${reserved} numéro(s) attribué(s)${alreadyReserved > 0 ? `, ${alreadyReserved} déjà attribué(s)` : ""}`);
      } else if (international === 0) {
        antdMessage.info("Tous les destinataires ont déjà un numéro de suivi");
      }
      if (international > 0) {
        antdMessage.warning(
          `${international} destinataire(s) à l'étranger : leur numéro de suivi se saisit à la main (colonne « N° de suivi »)`,
          8
        );
      }
    } catch (error) {
      antdMessage.error(error.message || "Erreur lors de l'attribution des numéros");
    } finally {
      setIsReserving(false);
    }
  }

  // Avant de générer le lot, avertit l'opérateur si des destinataires n'ont pas de numéro
  // de suivi (ils seront ignorés). L'ouverture de l'onglet se fait dans onOk (geste
  // utilisateur → évite le blocage popup).
  function handleGenerateLiasses(preview) {
    if (!preview) {
      // Blocage : chaque destinataire imprimé (avec n° de suivi) doit avoir une adresse
      // de retour (posée à la création : société ou personnalisée). Sinon, on refuse —
      // l'adresse est déjà sur le destinataire, aucun appel société nécessaire.
      const withNumber = recipients.filter((r) => r.trackingNumber);
      const missingReturn = withNumber.filter(
        (r) => !r.return_address_line_1 && !r.return_address_name
      );
      if (missingReturn.length > 0) {
        antdMessage.error(
          `Adresse de retour manquante pour ${missingReturn.length} destinataire(s) sur ${withNumber.length}. ` +
            "Impossible de générer la liasse."
        );
        return;
      }

      const missing = recipients.filter((r) => !r.trackingNumber).length;
      if (missing > 0) {
        Modal.confirm({
          title: "Numéros de suivi manquants",
          content: `${missing} destinataire(s) sur ${recipients.length} n'ont pas de numéro de suivi attribué et seront ignorés dans la liasse. Générer quand même ?`,
          okText: "Générer quand même",
          cancelText: "Annuler",
          onOk: () => doGenerateLiasses(preview),
        });
        return;
      }
    }
    doGenerateLiasses(preview);
  }

  async function doGenerateLiasses(preview) {
    setGeneratingMode(preview ? "preview" : "lot");
    // Onglet ouvert pendant le geste utilisateur (évite le blocage popup après await)
    const win = window.open("", "_blank");
    try {
      const result = await generateLiasses({ companyId: company, messageId: id, preview });
      const { url, pdf, generated, skipped } = result.data;
      // Prod : URL du bucket. Aperçu/calage : PDF base64 (léger, à l'écran).
      let target = url;
      if (!target && pdf) {
        const bytes = atob(pdf);
        const buf = new Uint8Array(bytes.length);
        for (let i = 0; i < bytes.length; i++) buf[i] = bytes.charCodeAt(i);
        target = URL.createObjectURL(new Blob([buf], { type: "application/pdf" }));
      }
      if (win) win.location = target;
      else window.location.href = target;
      antdMessage.success(
        `${generated} liasse(s) générée(s)${skipped > 0 ? ` — ${skipped} ignoré(s) (sans n° ou saisie libre)` : ""}`
      );
    } catch (error) {
      if (win) win.close();
      antdMessage.error(error.message || "Erreur lors de la génération des liasses");
    } finally {
      setGeneratingMode(null);
    }
  }

  const columnsHead = [
    { title: "Nom", dataIndex: "last_name" },
    { title: "Prénom", dataIndex: "first_name" },
    {
      title: "Type",
      dataIndex: "type",
      render: (type) => (type === "individual" ? "Particulier" : "Professionnel"),
    },
  ];

  const columnsTail = [
    {
      title: "Status",
      dataIndex: "status",
      render: (status) => {
        switch (status) {
          case "prepare":
            return (
              <Tag icon={<ClockCircleOutlined />} color="processing">
                {message.date_type === "now" ? "En cours d'envoi" : "En préparation"}
              </Tag>
            );
          case "scheduled":
            return (
              <Tag icon={<ClockCircleOutlined />} color="processing">
                Programmé
              </Tag>
            );
          case "cancelled":
            return (
              <Tag icon={<CloseCircleOutlined />} color="error">
                Annulé
              </Tag>
            );
          default:
            return (
              <Tag icon={<CheckCircleOutlined />} color="success">
                Envoyé
              </Tag>
            );
        }
      },
    },
    {
      title: "Date de status",
      dataIndex: "last_updated",
      render: (lastUpdated) => moment(lastUpdated.toDate()).format("DD/MM/YYYY HH:mm"),
    },
  ];

  useEffect(() => {
    const messageRef = db.collection("companies").doc(company).collection("messages").doc(id);

    // Set up real-time listener for the message
    const unsubscribeMessage = messageRef.onSnapshot((snapshot) => {
      const data = snapshot.data();

      if (data) {
        persistedRef.current = {
          trackingNumber: data.trackingNumber,
          pages: data.pages,
          addresses_count: data.addresses_count,
        };
        // Set up real-time listener for recipients
        const unsubscribeRecipients = messageRef
          .collection("recipients")
          .onSnapshot((recipientsSnapshot) => {
            const recipientsObj = {};
            const recipientsList = [];

            recipientsSnapshot.docs.forEach((recipientDoc) => {
              const recipient = { ...recipientDoc.data(), key: recipientDoc.id };
              recipientsList.push(recipient);

              if (recipientsObj[recipient.message.id]) {
                recipientsObj[recipient.message.id].push(recipient);
              } else {
                recipientsObj[recipient.message.id] = [recipient];
              }
            });

            setRecipients(recipientsList);

            // Check if status changed from "prepare" to something else
            // Use the first recipient's status as the message status
            const currentStatus = recipientsList.length > 0 ? recipientsList[0].status : null;

            // Only show modal if another agent completed the envoi (not the current user)
            if (previousStatus === "prepare" && currentStatus && currentStatus !== "prepare" && !isStatusChangeInitiator) {
              setShowHandledModal(true);
              setCountdown(300); // Reset countdown to 5 minutes
            }

            // Reset initiator flag after status change is processed
            if (isStatusChangeInitiator && currentStatus !== "prepare") {
              setIsStatusChangeInitiator(false);
            }

            // Update previous status
            if (currentStatus) {
              setPreviousStatus(currentStatus);
            }

            data.user.get().then((userSentSnapshot) => {
              db.collection("companies")
                .doc(company)
                .get()
                .then((companySnap) => {
                  setMessage({
                    id: snapshot.id,
                    key: snapshot.id,
                    ...data,
                    sentBy: userSentSnapshot.data(),
                    userCompany: { ...companySnap.data(), id: companySnap.id },
                    companyId: companySnap.id,
                    recipients: recipientsObj[snapshot.id],
                  });
                });
            });
          });

        // Return cleanup function for recipients listener
        return unsubscribeRecipients;
      } else {
        history.replace("/historique");
      }
    });

    // Cleanup function - unsubscribe from message listener
    return () => {
      unsubscribeMessage();
    };
  }, [id, company, db, history, previousStatus, isStatusChangeInitiator]);

  // Countdown timer effect
  useEffect(() => {
    if (showHandledModal && countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);

      return () => clearTimeout(timer);
    } else if (showHandledModal && countdown === 0) {
      // Close the tab/window when countdown reaches 0
      window.close();

      // Fallback: if window.close() doesn't work (e.g., not opened by script),
      // redirect to history page
      setTimeout(() => {
        history.push("/admin/sent");
      }, 100);
    }
  }, [showHandledModal, countdown, history]);

  function onWriteError(e) {
    console.error(e);
    antdMessage.error("Enregistrement impossible. Veuillez réessayer.");
  }

  // Champs opérateur enregistrés à la validation (sortie du champ ou Entrée) et non plus à
  // chaque frappe : une saisie = une écriture = une entrée au journal d'audit.
  function saveOperatorField(field, value) {
    if (persistedRef.current[field] === value) return;
    const previous = persistedRef.current[field];
    persistedRef.current = { ...persistedRef.current, [field]: value };
    messageDocRef()
      .set({ [field]: value, last_change: stamp() }, { merge: true })
      .catch((e) => {
        persistedRef.current = { ...persistedRef.current, [field]: previous };
        onWriteError(e);
      });
  }

  function saveCount(field) {
    const v = parseInt(message[field], 10);
    if (Number.isInteger(v) && v >= 1) saveOperatorField(field, v);
  }

  const onTrackingNumberChange = (e) => {
    setMessage({
      ...message,
      trackingNumber: e.target.value,
    });
  };

  const saveTrackingNumber = () => {
    saveOperatorField("trackingNumber", message.trackingNumber || "");
  };

  const onTrackingTypeUpdate = (type) => {
    messageDocRef()
      .set({ trackingType: type, last_change: stamp() }, { merge: true })
      .then(() => {
        //update locally
        setMessage({
          ...message,
          trackingType: type,
        });
      })
      .catch(onWriteError);
  };

  const onTrackingFileUpload = async (file) => {
    const name = `${Date.now()}-${file.name}`;
    const storageRef = firebase.storage().ref("tracking-proofs/" + user.uid + "/" + name);
    await storageRef.put(file);
    const url = await storageRef.getDownloadURL();
    messageDocRef()
      .set(
        {
          trackingFile: url,
          trackingPath: "tracking-proofs/" + user.uid + "/" + name,
          last_change: stamp(),
        },
        { merge: true }
      )
      .then(() => {
        //update locally
        setMessage({
          ...message,
          trackingFile: url,
          trackingPath: "tracking-proofs/" + user.uid + "/" + name,
        });
      })
      .catch(onWriteError);
  };
  const onTrackingFileReset = () => {
    messageDocRef()
      .set(
        {
          trackingFile: firebase.firestore.FieldValue.delete(),
          trackingPath: firebase.firestore.FieldValue.delete(),
          last_change: stamp(),
        },
        { merge: true }
      )
      .then(() => {
        //update locally
        const newMessage = { ...message };
        delete newMessage.trackingFile;
        delete newMessage.trackingPath;
        setMessage(newMessage);
        const storageRef = firebase.storage().ref(message.trackingPath);
        return storageRef.delete();
      })
      .catch(onWriteError);
  };

  // Helper function to format countdown time
  const formatCountdown = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
  };

  return message === null ? (
    <div>Loading</div>
  ) : (
    <>
      <Modal
        title="Courrier traité"
        visible={showHandledModal}
        closable={false}
        footer={[
          <Button
            key="close"
            type="primary"
            onClick={() => {
              window.close();
              // Fallback redirect
              setTimeout(() => {
                history.push("/admin/sent");
              }, 100);
            }}
          >
            Fermer maintenant
          </Button>,
        ]}
      >
        <p>
          Ce courrier a déjà été traité et marqué comme envoyé. Cette page va se fermer
          automatiquement dans :
        </p>
        <div
          style={{ textAlign: "center", fontSize: "24px", fontWeight: "bold", margin: "20px 0" }}
        >
          {formatCountdown(countdown)}
        </div>
        <p style={{ fontSize: "12px", color: "#888", marginTop: "16px" }}>
          Vous pouvez fermer cette page manuellement en cliquant sur le bouton ci-dessous.
        </p>
      </Modal>

      <PageHeader
        onBack={() => {
          history.push("/admin/sent");
        }}
        style={{ padding: 0, marginBottom: 24 }}
        title={`Détails de l'envoi N°${id.toUpperCase()}`}
      />

      <Descriptions bordered title="Informations sur l'envoi" size="small" column={1}>
        <Descriptions.Item
          label={
            <LabelContainer>
              <NumberOutlined />
              N° d'envoi
            </LabelContainer>
          }
        >
          {id.toUpperCase()}
        </Descriptions.Item>
        <Descriptions.Item
          l
          label={
            <LabelContainer>
              <BsFillPersonFill />
              Déclenché par
            </LabelContainer>
          }
        >
          {senderLabel(message.user, message.sentBy)}
        </Descriptions.Item>
        {message.userCompany && (
          <Descriptions.Item
            label={
              <LabelContainer>
                <BiBuildings />
                Entité émettrice
              </LabelContainer>
            }
          >
            {message.userCompany.name}
            <br />
            {message.userCompany.address_line_1}
            <br />
            {message.userCompany.address_line_2 && (
              <>
                {message.userCompany.address_line_2}
                <br />
              </>
            )}
            {message.userCompany.postal_code} {message.userCompany.city}
            <br />
          </Descriptions.Item>
        )}
        <Descriptions.Item
          label={
            <LabelContainer>
              <AiFillDatabase />
              Status
            </LabelContainer>
          }
          contentStyle={{
            padding: 0,
          }}
        >
          <ChangeStatusDropdown
            record={message}
            onConfirm={() => {
              setUpdateTrigger(Date.now);
            }}
            onStatusChangeInitiated={() => {
              setIsStatusChangeInitiator(true);
            }}
          />
        </Descriptions.Item>
        {message.type === "recommande" && (
          <Descriptions.Item
            label={
              <LabelContainer>
                <MdLocalPostOffice />
                N° de suivi
              </LabelContainer>
            }
          >
            <Row>
              <div style={{ flex: 1 }}>
                <Input
                  placeholder="Numéro de suivi"
                  style={{ maxWidth: "15rem" }}
                  onChange={onTrackingNumberChange}
                  onBlur={saveTrackingNumber}
                  onPressEnter={saveTrackingNumber}
                  value={message.trackingNumber}
                />
                <Select
                  style={{ width: "10rem" }}
                  onChange={onTrackingTypeUpdate}
                  value={message.trackingType}
                  placeholder="Expéditeur"
                >
                  <Select.Option value="laposte">La Poste</Select.Option>
                  <Select.Option value="solidoc" disabled={true}>
                    Solidoc
                  </Select.Option>
                </Select>
              </div>
              <div style={{ display: "flex", alignItems: "center" }}>
                {!message.trackingFile && (
                  <Upload action={onTrackingFileUpload} showUploadList={false}>
                    <Button>Ajouter la preuve de dépôt</Button>
                  </Upload>
                )}
                {message.trackingFile && (
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <a target="_blank" href={message.trackingFile} style={{ marginRight: "1rem" }}>
                      Preuve de dépôt
                    </a>

                    <Button
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                      type="primary"
                      title="Supprimer la preuve de dépôt"
                      size="small"
                      shape="circle"
                      icon={<BsTrashFill />}
                      onClick={onTrackingFileReset}
                    />
                  </div>
                )}
              </div>
            </Row>
          </Descriptions.Item>
        )}
      </Descriptions>

      {(message.type === "simple" || message.type === "recommande") && (
        <>
          <Descriptions
            bordered
            title="Options d'impression"
          size="small"
          style={{ marginTop: 32 }}
          column={2}
        >
          <Descriptions.Item
            label={
              <LabelContainer>
                <IoIosColorPalette />
                Paramètre couleur
              </LabelContainer>
            }
            span={1}
          >
            {message.color_option === "black_white" ? "Noir/Blanc" : "Couleur"}
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <LabelContainer>
                <RiFileCopyLine />
                Recto ou Recto/Verso
              </LabelContainer>
            }
            span={1}
          >
            {message.face_option === "one_face" ? "Recto seulement" : "Recto/Verso"}
          </Descriptions.Item>
          {message.type === "simple" && (
            <>
              <Descriptions.Item
                label={
                  <LabelContainer>
                    <BsPeopleFill />
                    Type de destinataires
                  </LabelContainer>
                }
                span={1}
              >
                {message.recipients_type === "unique"
                  ? "Destinataire unique"
                  : "Destinataires multiples"}
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <LabelContainer>
                    <AiFillClockCircle />
                    Délai de distribution
                  </LabelContainer>
                }
                span={1}
              >
                {message.envelope}
              </Descriptions.Item>
            </>
          )}
          {message.type === "recommande" && (
            <>
              <Descriptions.Item
                label={
                  <LabelContainer>
                    <IoMdMail />
                    Accusé de réception
                  </LabelContainer>
                }
                span={1}
              >
                {message.receipt_aknowledge === "yes"
                  ? "Avec accusé de réception"
                  : "Sans accusé de réception"}
              </Descriptions.Item>
              <Descriptions.Item
                label={
                  <LabelContainer>
                    <SignalFilled />
                    Niveau de recommandation
                  </LabelContainer>
                }
                span={1}
              >
                {message.level}
              </Descriptions.Item>
            </>
          )}
        </Descriptions>

        <Descriptions
          bordered
          title="Impression (Opérateur)"
          size="small"
          style={{ marginTop: 32 }}
          column={2}
        >
          <Descriptions.Item
            label={
              <LabelContainer>
                <RiFileCopy2Line />
                Nombre de pages imprimées
              </LabelContainer>
            }
            span={1}
            contentStyle={{ padding: 0 }}
          >
            <InputNumber
              min={1}
              step={1}
              precision={0}
              value={message.pages || undefined}
              placeholder="À renseigner"
              onChange={(value) => setMessage({ ...message, pages: value })}
              onBlur={() => saveCount("pages")}
              onPressEnter={() => saveCount("pages")}
              style={{ width: "100%" }}
            />
          </Descriptions.Item>
          <Descriptions.Item
            label={
              <LabelContainer>
                <BsPeopleFill />
                Nombre d'adresses / MSP
              </LabelContainer>
            }
            span={1}
            contentStyle={{ padding: 0 }}
          >
            <InputNumber
              min={1}
              step={1}
              precision={0}
              value={message.addresses_count || undefined}
              placeholder="À renseigner"
              onChange={(value) => setMessage({ ...message, addresses_count: value })}
              onBlur={() => saveCount("addresses_count")}
              onPressEnter={() => saveCount("addresses_count")}
              style={{ width: "100%" }}
            />
          </Descriptions.Item>
        </Descriptions>
        </>
      )}

      {message.type === "electronique" && (
        <>
          <Descriptions
            bordered
            title="Options d'envoi"
            size="small"
            style={{ marginTop: 32 }}
            column={2}
          >
            <Descriptions.Item label="Type d'envoi" span={message.date_type === "now" ? 2 : 1}>
              {message.date_type === "now" ? "Immédiat" : "Différé"}
            </Descriptions.Item>
            {message.date_type !== "now" && message.date && (
              <Descriptions.Item label="Date d'envoi" span={1}>
                <span style={{ textTransform: "capitalize" }}>
                  {moment(message.date.toDate()).format("dddd D MMMM YYYY")}
                </span>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Référence 1" span={1}>
              {message.ref1 || "Aucune"}
            </Descriptions.Item>
            <Descriptions.Item label="Référence 2" span={1}>
              {message.ref2 || "Aucune"}
            </Descriptions.Item>
          </Descriptions>

          <Descriptions style={{ marginTop: 32 }} title="Destinataires" />
          <RecipientsTable data={recipients} />
        </>
      )}

      {message.type === "recommande" && recipients.length > 0 && (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 32 }}>
            <h3 style={{ margin: 0 }}>Destinataires et suivi</h3>
            <Space>
              {recipients.some((r) => !r.trackingNumber) && (
                <Button
                  type="primary"
                  loading={isReserving}
                  onClick={handleBatchReserve}
                >
                  Attribuer n° AR
                </Button>
              )}
              {recipients.some((r) => r.trackingNumber) && (
                <>
                  <Button
                    loading={generatingMode === "preview"}
                    disabled={generatingMode === "lot"}
                    onClick={() => handleGenerateLiasses(true)}
                  >
                    Aperçu (calage)
                  </Button>
                  <Button
                    loading={generatingMode === "lot"}
                    disabled={generatingMode === "preview"}
                    onClick={() => handleGenerateLiasses(false)}
                  >
                    Générer le lot
                  </Button>
                </>
              )}
            </Space>
          </div>
          <RecipientsTable
            data={recipients}
            messageType="recommande"
            companyId={company}
            messageId={id}
          />
        </>
      )}

      {message.comment && (
        <>
          <Descriptions style={{ marginTop: 32 }} title="Instructions d'impression" />

          <Card type="inner" style={{ width: "100%" }} bodyStyle={{ padding: "8px 24px 0" }}>
            <Row style={{ opacity: 0.5, fontSize: "0.8em", fontWeight: "bold" }}>
              ex : Agrafer des documents, inclure une référence dans une liasse recommandée, pièce
              jointe à insérer dans chaque pli.
            </Row>
            {message.comment}
          </Card>
        </>
      )}
      <Descriptions
        style={{ marginTop: 32 }}
        title={message.type === "electronique" ? "Pièces jointes" : "Documents"}
      />
      <Card type="inner" style={{ width: "100%" }} bodyStyle={{ padding: "8px 24px 0" }}>
        <Row gutter={[8, 8]}>
          <Col span={24}>
            <List
              itemLayout="horizontal"
              dataSource={
                message.attachments.length
                  ? message.attachments
                  : [
                      {
                        filename:
                          message.type === "electronique"
                            ? "Aucune pièce jointe"
                            : "Aucun document",
                      },
                    ]
              }
              renderItem={(item, index) => (
                <List.Item>
                  <List.Item.Meta
                    avatar={message.attachments.length ? getFileIcon(item.mimetype) : null}
                    title={
                      <>
                        {item.filename}
                        {item.pages && (
                          <Tag style={{ marginLeft: 8 }}>
                            {item.pages} page{item.pages > 1 ? "s" : ""}
                          </Tag>
                        )}
                      </>
                    }
                    description={
                      message.attachments.length ? (
                        <a href={item.url} rel="noopener noreferrer" target="_blank">
                          Ouvrir le fichier
                        </a>
                      ) : null
                    }
                  />

                  <div>
                    {item.completed ? (
                      <Tag icon={<CheckCircleOutlined />} color="cyan">
                        Traité
                      </Tag>
                    ) : (
                      <Button
                        type="primary"
                        title="Marquer comme envoyer"
                        size="small"
                        shape="circle"
                        icon={<CheckCircleOutlined />}
                        onClick={() => {
                          const attachments = JSON.parse(JSON.stringify(message.attachments)); // deep copy
                          attachments.splice(index, 1, {
                            ...attachments[index],
                            completed: true,
                          });

                          return messageDocRef()
                            .set({ attachments: attachments, last_change: stamp() }, { merge: true })
                            .then(() => {
                              //update locally
                              setMessage({
                                ...message,
                                attachments,
                              });
                            })
                            .catch(onWriteError);
                        }}
                      />
                    )}
                  </div>
                </List.Item>
              )}
            />
          </Col>
        </Row>
        {(message.type === "simple" || message.type === "recommande") && message.attachments.length > 0 && (
          <Row style={{ marginTop: 8, padding: "0 8px", fontWeight: "bold" }}>
            Total pages documents : {message.attachments.reduce((total, a) => total + (a.pages || 0), 0) || "Non renseigné"}
          </Row>
        )}
      </Card>

      {message.type === "electronique" && (
        <Descriptions
          bordered
          title="Contenu du message"
          size="small"
          column={1}
          style={{ marginTop: 32 }}
        >
          <Descriptions.Item label="Objet" span={1}>
            {message.subject}
          </Descriptions.Item>
          <Descriptions.Item label="Message" span={1}>
            {message.content}
          </Descriptions.Item>
        </Descriptions>
      )}

      {/* Journal d'audit de l'envoi : admins Solidoc uniquement (décision 05/10/2026) */}
      {user && user.isAdmin && (
        <>
          <Descriptions style={{ marginTop: 32 }} title="Historique (journal d'audit)" />
          <AuditTimeline companyId={company} messageId={id} />
        </>
      )}
    </>
  );
}
